import { useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { formatPhone } from "./lib/phone.js";

function when(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function ConversationThread({ conversation, onSend }) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function send() {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await onSend(conversation.number, body);
      setDraft("");
    } catch (e) {
      setError(e?.body?.detail || e?.message || "The kernel refused this send.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack sx={{ minWidth: 0, height: "100%" }}>
      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
          {conversation.person?.displayName || formatPhone(conversation.number)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatPhone(conversation.number)}
          {conversation.person ? "" : " · not in your contacts"}
        </Typography>
      </Box>

      <Stack spacing={1.5} sx={{ p: 2, flex: 1, overflowY: "auto", minHeight: 0 }}>
        {conversation.messages.map((m) => (
          <Box key={m.id} sx={{ alignSelf: m.direction === "outbound" ? "flex-end" : "flex-start", maxWidth: "70%" }}>
            <Box
              sx={{
                bgcolor: m.direction === "outbound" ? "primary.main" : "action.hover",
                color: m.direction === "outbound" ? "primary.contrastText" : "text.primary",
                borderRadius: 2,
                px: 1.5,
                py: 1,
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {m.body}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: m.direction === "outbound" ? "right" : "left", mt: 0.25 }}
            >
              {m.direction === "outbound" ? "Sent" : "Received"} · {when(m.sentAt)}
              {m.pending ? " · sending" : ""}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Reply"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={busy}
        />
        <Button variant="contained" onClick={send} disabled={busy || !draft.trim()}>
          Send
        </Button>
      </Stack>
      {error ? (
        <Alert severity="error" variant="outlined" sx={{ mx: 2, mb: 2 }}>
          {error}
        </Alert>
      ) : null}
    </Stack>
  );
}
